import React, { useEffect } from 'react';
import { IconClose, IconSearch } from './icons';

/* ── Surfaces ──────────────────────────────────────────────────────────────── */

export const Card = ({ className = '', children, ...rest }) => (
  <section
    className={`rounded-xl border border-ink-200 bg-white shadow-card ${className}`}
    {...rest}
  >
    {children}
  </section>
);

export const CardHeader = ({ title, description, actions, className = '' }) => (
  <header
    className={`flex flex-wrap items-start justify-between gap-4 border-b border-ink-200 px-5 py-4 ${className}`}
  >
    <div className="min-w-0">
      <h2 className="font-display text-base font-700 tracking-tight text-ink-900">{title}</h2>
      {description && <p className="mt-1 text-sm text-ink-500">{description}</p>}
    </div>
    {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
  </header>
);

export const CardBody = ({ className = '', children }) => (
  <div className={`px-5 py-5 ${className}`}>{children}</div>
);

/* ── Buttons ───────────────────────────────────────────────────────────────── */

const buttonVariants = {
  primary: 'bg-brand-800 text-white hover:bg-brand-700 focus-visible:ring-brand-600',
  gold: 'bg-gold-500 text-brand-900 hover:bg-gold-400 focus-visible:ring-gold-600',
  secondary:
    'border border-ink-300 bg-white text-ink-700 hover:bg-ink-50 hover:text-ink-900 focus-visible:ring-brand-600',
  ghost: 'text-ink-600 hover:bg-ink-100 hover:text-ink-900 focus-visible:ring-brand-600',
  danger: 'bg-danger-600 text-white hover:bg-danger-700 focus-visible:ring-danger-600',
  dangerGhost: 'text-danger-600 hover:bg-danger-50 focus-visible:ring-danger-600',
  // The courier portal's identity colour. Admin and merchant screens use
  // info only for state, so it appears nowhere else as a button.
  info: 'bg-info-600 text-white hover:bg-info-700 focus-visible:ring-info-600'
};

const buttonSizes = {
  sm: 'h-8 gap-1.5 px-3 text-[13px]',
  md: 'h-10 gap-2 px-4 text-sm',
  lg: 'h-11 gap-2 px-5 text-sm'
};

/**
 * `as` renders Button's styling on another element/component (typically
 * react-router's Link, for a button-styled navigation link) instead of a
 * native <button>. Without this, `as`/`to` land on the DOM as inert,
 * non-functional attributes on a real <button> — it renders looking right
 * and does nothing on click. site/ui.js's Button already does this
 * correctly; this mirrors it so the two kits' Buttons behave identically.
 */
export const Button = ({
  as: Component = 'button',
  variant = 'primary',
  size = 'md',
  className = '',
  type = 'button',
  loading = false,
  disabled = false,
  children,
  ...rest
}) => {
  const isNativeButton = Component === 'button';
  return (
    <Component
      {...(isNativeButton ? { type, disabled: disabled || loading } : {})}
      className={`inline-flex select-none items-center justify-center rounded-lg font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:pointer-events-none disabled:opacity-50 ${buttonVariants[variant]} ${buttonSizes[size]} ${className}`}
      {...rest}
    >
      {loading && <Spinner className="h-4 w-4" />}
      {children}
    </Component>
  );
};

export const Spinner = ({ className = 'h-4 w-4' }) => (
  <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

/* ── Status ────────────────────────────────────────────────────────────────── */

const pillTones = {
  success: 'bg-success-50 text-success-700 ring-success-100',
  warning: 'bg-warning-50 text-warning-700 ring-warning-100',
  danger: 'bg-danger-50 text-danger-700 ring-danger-100',
  info: 'bg-info-50 text-info-700 ring-info-100',
  neutral: 'bg-ink-100 text-ink-600 ring-ink-200'
};

const dotTones = {
  success: 'bg-success-600',
  warning: 'bg-warning-600',
  danger: 'bg-danger-600',
  info: 'bg-info-600',
  neutral: 'bg-ink-400'
};

/**
 * Status is never carried by colour alone — every pill ships its own label, and
 * the dot is a second (position/shape) cue for colour-vision-deficient readers.
 */
export const StatusPill = ({ tone = 'neutral', dot = true, children, className = '' }) => (
  <span
    className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium capitalize ring-1 ring-inset ${pillTones[tone]} ${className}`}
  >
    {dot && <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotTones[tone]}`} />}
    {children}
  </span>
);

/* ── Stat tile ─────────────────────────────────────────────────────────────── */

/**
 * A headline number, not a chart: the value wears ink (never a series colour),
 * so the eye reads magnitude from the digits and tone only from the small icon.
 */
export const StatTile = ({ label, value, hint, icon: Icon, tone = 'neutral' }) => (
  <div className="rounded-xl border border-ink-200 bg-white p-5 shadow-card">
    <div className="flex items-start justify-between gap-3">
      <p className="text-sm font-medium text-ink-500">{label}</p>
      {Icon && (
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${pillTones[tone]} ring-1 ring-inset`}
        >
          <Icon size={16} />
        </span>
      )}
    </div>
    <p className="mt-3 font-display text-3xl font-700 tracking-tight text-ink-900 tabular-nums">
      {value}
    </p>
    {hint && <p className="mt-1 text-xs text-ink-500">{hint}</p>}
  </div>
);

/* ── List screens ──────────────────────────────────────────────────────────── */

/**
 * The control strip for a list screen: filters on the left, actions on the
 * right, sitting directly on top of the table it filters.
 *
 * Deliberately one thin row rather than the card-of-filters-plus-grid-of-stat-
 * tiles these screens used to open with — between them those took most of the
 * first screen, so the data being managed started below the fold. Figures that
 * earned their place moved into the header as InlineStats; the rest went.
 */
export const FilterBar = ({ children, actions, className = '' }) => (
  <div
    className={`flex flex-wrap items-center gap-2 border-b border-ink-200 bg-ink-50/50 px-5 py-2.5 ${className}`}
  >
    {children}
    {actions && <div className="ml-auto flex flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

/** Shared sizing for everything that sits in a FilterBar. */
const filterControl =
  'h-9 rounded-lg border border-ink-300 bg-white text-[13px] text-ink-900 transition-colors ' +
  'focus-within:border-brand-600 focus-within:ring-4 focus-within:ring-brand-600/10';

/**
 * A select whose label sits inside it. One line instead of the two a stacked
 * Field costs, and the label stays visible once a value is picked — which a
 * bare placeholder option does not manage.
 */
export const FilterSelect = ({ label, value, onChange, children, className = '' }) => (
  <label className={`inline-flex items-center ${filterControl} ${className}`}>
    <span className="whitespace-nowrap pl-3 pr-1.5 text-ink-500">{label}</span>
    <select
      value={value}
      onChange={onChange}
      aria-label={label}
      // Capped so one long option (a merchant's full name) can't stretch the
      // bar and push the rest of the filters onto a second row; the native
      // control truncates on its own.
      className="pb-select h-full w-auto max-w-[9rem] cursor-pointer truncate rounded-r-lg border-0 bg-transparent py-0 pl-0 text-[13px] font-500 text-ink-900 focus:outline-none focus:ring-0"
    >
      {children}
    </select>
  </label>
);

/** Search box for a FilterBar, with the magnifier inside the control. */
export const FilterSearch = ({ value, onChange, placeholder = 'Search…', className = '' }) => (
  <label className={`inline-flex items-center ${filterControl} ${className}`}>
    <span className="pl-2.5 pr-1.5 text-ink-500">
      <IconSearch size={15} />
    </span>
    <input
      type="search"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      aria-label={placeholder}
      className="h-full w-full min-w-0 rounded-r-lg border-0 bg-transparent pr-3 text-[13px] text-ink-900 placeholder:text-ink-500 focus:outline-none focus:ring-0"
    />
  </label>
);

/**
 * Key figures as one line in a card header — the same numbers the stat-tile
 * grid carried, at a tenth of the height. Pass only figures worth the space:
 * a count the pagination already states is not one.
 */
export const InlineStats = ({ items = [] }) => (
  <dl className="flex flex-wrap items-center gap-x-5 gap-y-1">
    {items.filter(Boolean).map((item) => (
      <div key={item.label} className="flex items-baseline gap-1.5">
        <dt className="text-xs text-ink-500">{item.label}</dt>
        <dd className="font-display text-sm font-700 tabular-nums text-ink-900">{item.value}</dd>
      </div>
    ))}
  </dl>
);

/**
 * The footer of a paged list. Says which rows are on screen out of how many,
 * which "page 2 of 7" alone never does.
 */
export const Pagination = ({ page, totalPages, total, perPage, shown, onPage }) => {
  if (!totalPages || totalPages <= 1) {
    if (!total) return null;
    return (
      <div className="border-t border-ink-200 px-5 py-3 text-xs text-ink-500">
        {total} {total === 1 ? 'result' : 'results'}
      </div>
    );
  }

  const first = (page - 1) * perPage + 1;
  const last = (page - 1) * perPage + (shown ?? perPage);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-ink-200 px-5 py-3">
      <p className="text-xs text-ink-500">
        {first}–{last} of {total}
      </p>
      <div className="flex items-center gap-2">
        <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          Previous
        </Button>
        <span className="text-xs tabular-nums text-ink-500">
          {page} / {totalPages}
        </span>
        <Button
          variant="secondary"
          size="sm"
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

/* ── Table ─────────────────────────────────────────────────────────────────── */

export const TableWrap = ({ children, className = '' }) => (
  <div className={`overflow-x-auto ${className}`}>
    <table className="min-w-full border-collapse text-sm">{children}</table>
  </div>
);

export const Th = ({ children, className = '', ...rest }) => (
  <th
    scope="col"
    className={`whitespace-nowrap border-b border-ink-200 bg-ink-50 px-5 py-3 text-left text-[11px] font-600 uppercase tracking-[0.06em] text-ink-500 ${className}`}
    {...rest}
  >
    {children}
  </th>
);

export const Td = ({ children, className = '', ...rest }) => (
  <td className={`border-b border-ink-100 px-5 py-4 align-middle text-ink-700 ${className}`} {...rest}>
    {children}
  </td>
);

export const Tr = ({ children, className = '', ...rest }) => (
  <tr className={`transition-colors hover:bg-brand-50/60 ${className}`} {...rest}>
    {children}
  </tr>
);

/* ── Empty & loading states ────────────────────────────────────────────────── */

export const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
    {Icon && (
      <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-100">
        <Icon size={22} />
      </span>
    )}
    <p className="font-display text-base font-700 text-ink-900">{title}</p>
    {description && <p className="mt-1.5 max-w-sm text-sm text-ink-500">{description}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse rounded-md bg-ink-100 ${className}`} />
);

export const TableSkeleton = ({ rows = 6, cols = 5 }) => (
  <div className="px-5 py-4">
    {Array.from({ length: rows }).map((_, r) => (
      <div key={r} className="flex items-center gap-4 border-b border-ink-100 py-3.5 last:border-0">
        {Array.from({ length: cols }).map((_, c) => (
          <Skeleton key={c} className={`h-3.5 ${c === 0 ? 'w-28' : 'w-full max-w-[9rem]'}`} />
        ))}
      </div>
    ))}
  </div>
);

/* ── Form controls ─────────────────────────────────────────────────────────── */

/**
 * Labelled form field.
 *
 * Marking a Field `required` shows the indicator and sets aria-required on the
 * control; passing `error` replaces the hint with the message, flags the
 * control aria-invalid (which .pb-control styles red) and links the two with
 * aria-describedby — so the wiring cannot drift from the visuals.
 */
export const Field = ({ label, hint, error, required, htmlFor, className = '', children }) => {
  const errorId = error && htmlFor ? `${htmlFor}-error` : undefined;
  const hintId = !error && hint && htmlFor ? `${htmlFor}-hint` : undefined;

  const control = React.isValidElement(children)
    ? React.cloneElement(children, {
        'aria-invalid': error ? true : children.props['aria-invalid'],
        'aria-describedby': errorId || hintId || children.props['aria-describedby'],
        'aria-required': required || children.props['aria-required'] || undefined
      })
    : children;

  return (
    <div className={className}>
      {label && (
        <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-ink-700">
          {label}
          {required && (
            <>
              <span className="ml-0.5 text-danger-600" aria-hidden="true">*</span>
              <span className="sr-only"> (required)</span>
            </>
          )}
        </label>
      )}
      {control}
      {error ? (
        <p id={errorId} role="alert" className="mt-1.5 text-xs font-medium text-danger-700">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="mt-1.5 text-xs text-ink-500">
          {hint}
        </p>
      ) : null}
    </div>
  );
};

export const Input = ({ className = '', ...rest }) => (
  <input className={`pb-control ${className}`} {...rest} />
);

export const Select = ({ className = '', children, ...rest }) => (
  <select className={`pb-control pb-select ${className}`} {...rest}>
    {children}
  </select>
);

export const Textarea = ({ className = '', ...rest }) => (
  <textarea className={`pb-control min-h-[96px] py-2.5 ${className}`} {...rest} />
);

export const Toggle = ({ checked, onChange, label, description, id }) => (
  <div className="flex items-start justify-between gap-6">
    <div className="min-w-0">
      <label htmlFor={id} className="text-sm font-medium text-ink-800">
        {label}
      </label>
      {description && <p className="mt-0.5 text-xs text-ink-500">{description}</p>}
    </div>
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 ${
        checked ? 'bg-brand-700' : 'bg-ink-300'
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-[22px]' : 'translate-x-[2px]'
        }`}
      />
    </button>
  </div>
);

/* ── Modal ─────────────────────────────────────────────────────────────────── */

const modalSizes = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
  xl: 'max-w-5xl'
};

/**
 * Dialog.
 *
 * Clicking the backdrop does NOT close it: these dialogs hold half-filled forms
 * and destructive confirmations, and a stray click outside would discard the
 * work. Escape still closes — that is a deliberate gesture, and the only way a
 * keyboard user can dismiss a dialog — as do the header X and any Cancel button.
 */
export const Modal = ({ open, onClose, title, description, size = 'md', footer, children }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-brand-950/50 p-0 backdrop-blur-[2px] sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={typeof title === 'string' ? title : undefined}
    >
      <div
        className={`w-full ${modalSizes[size]} animate-rise rounded-t-2xl bg-white shadow-panel sm:rounded-2xl`}
      >
        <header className="flex items-start justify-between gap-4 border-b border-ink-200 px-6 py-4">
          <div className="min-w-0">
            <h2 className="font-display text-lg font-700 tracking-tight text-ink-900">{title}</h2>
            {description && <p className="mt-1 text-sm text-ink-500">{description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="-mr-1.5 -mt-1 rounded-lg p-2 text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
          >
            <IconClose size={18} />
          </button>
        </header>
        <div className="max-h-[70vh] overflow-y-auto px-6 py-5">{children}</div>
        {footer && (
          <footer className="flex flex-wrap items-center justify-end gap-3 border-t border-ink-200 bg-ink-50/60 px-6 py-4">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
};

/* ── Misc ──────────────────────────────────────────────────────────────────── */

export const SectionTitle = ({ children, className = '' }) => (
  <h3 className={`font-display text-sm font-700 uppercase tracking-[0.08em] text-ink-500 ${className}`}>
    {children}
  </h3>
);

export const DescriptionItem = ({ label, children }) => (
  <div className="min-w-0">
    <dt className="text-xs font-medium uppercase tracking-wide text-ink-500">{label}</dt>
    <dd className="mt-1 break-words text-sm text-ink-800">{children}</dd>
  </div>
);
